import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, Loader2, X } from "lucide-react";
import type { MergeDuplicatesResponse } from "@ckgo2/shared/types";
import { api, type ApiResponse } from "@/api/client";
import { runMergeBatches, type MergeRunState } from "./mergeBatches";

const MERGE_ENDPOINT = "/api/admin/backfill-media/merge-duplicates";

export interface MergeDuplicatesViewProps {
  preview: MergeDuplicatesResponse | undefined;
  loading: boolean;
  previewError: string | null;
  run: MergeRunState;
  onApply: () => void;
  onClose: () => void;
}

/**
 * Pohled dialogu sloučení duplicit (jen props, bez dotazů): náhled skupin, potvrzení, průběh
 * dávek a souhrn nebo chyba. Háčky E2E: `data-merge-dialog`, `-empty`, `-groups`
 * (`data-count`), `-apply`, `-progress` (`data-done`, `data-total`), `-error`.
 */
export function MergeDuplicatesView({
  preview,
  loading,
  previewError,
  run,
  onApply,
  onClose,
}: MergeDuplicatesViewProps) {
  const { t } = useTranslation();
  const running = run.phase === "running";
  const finished = run.phase === "done" || run.phase === "error";
  // Náhled starého API `hashes` nemá: bez nich není z čeho skládat dávky.
  const canApply =
    !loading && !previewError && !!preview && preview.groups > 0 && !!preview.hashes?.length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={running ? undefined : onClose}
    >
      <div
        data-merge-dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="merge-dialog-title"
        className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h2
            id="merge-dialog-title"
            className="text-lg font-semibold text-gray-900 dark:text-white"
          >
            {t("media.merge.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={running}
            aria-label={t("common.actions.close")}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-40"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {loading ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : previewError ? (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
              <p className="font-medium">{t("media.merge.previewFailed")}</p>
              <p className="mt-1">{previewError}</p>
            </div>
          ) : !preview || preview.groups === 0 ? (
            <p data-merge-empty className="py-10 text-center text-sm text-gray-500">
              {t("media.merge.noneFound")}
            </p>
          ) : (
            <div data-merge-groups data-count={preview.groups} className="space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t("media.merge.foundGroups", { count: preview.groups })} ·{" "}
                {t("media.merge.willDelete", { count: preview.duplicates })} ·{" "}
                {t("media.merge.willRepoint", { count: preview.entitiesTouched })}
              </p>
              <ul className="space-y-2">
                {preview.preview.map((g) => (
                  <li
                    key={g.hash}
                    className="rounded-lg border border-gray-200 p-2 text-xs dark:border-gray-700"
                  >
                    <p className="font-medium text-gray-900 dark:text-white">
                      {t("media.merge.keep", { name: g.canonicalAlt || g.canonicalId })}
                    </p>
                    <p className="text-gray-500">
                      {t("media.merge.removing", {
                        count: g.dupIds.length,
                        ids: g.dupIds.join(", "),
                      })}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {run.phase !== "idle" && (
            <p
              data-merge-progress
              data-done={run.done}
              data-total={run.total}
              className="text-sm font-medium text-gray-900 dark:text-white"
            >
              {t("media.merge.progress", { done: run.done, count: run.total })}
            </p>
          )}
          {run.phase === "done" && (
            <p className="text-sm text-green-700 dark:text-green-400">
              {t("media.merge.finished")}
            </p>
          )}
          {run.phase === "error" && (
            <div
              data-merge-error
              role="alert"
              className="flex gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p>{t("media.merge.failed", { done: run.done, count: run.total })}</p>
                <p className="mt-1">{run.message}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 p-4 dark:border-gray-700">
          {finished ? (
            <button type="button" onClick={onClose} className="btn-primary text-sm">
              {t("common.actions.close")}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={running}
                className="btn-ghost text-sm"
              >
                {t("common.actions.cancel")}
              </button>
              <button
                type="button"
                data-merge-apply
                onClick={onApply}
                disabled={running || !canApply}
                className="btn-primary text-sm"
              >
                {running ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {t("media.merge.apply", { count: preview?.duplicates ?? 0 })}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Dialog sloučení přesných duplicit na stránce Média (F7.13 „po dávkách“): načte náhled celé
 * knihovny, po potvrzení pošle jeho `hashes` dávkami `runMergeBatches` za sebou a při chybě
 * se zastaví. `onClose(changed)`: `changed` = běh začal, tj. aspoň jeden požadavek odešel
 * (stránka pak obnoví seznam, alba a zdroje). Ne „aspoň jedna dávka prošla“: server přepíše
 * odkazy na kanonickou ještě před transakcí zápisu, takže i selhaná nebo ztracená odpověď
 * první dávky mohla data změnit.
 */
export default function MergeDuplicatesDialog({
  onClose,
}: {
  onClose: (changed: boolean) => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["media-merge-dryrun"],
    queryFn: () => api.post<ApiResponse<MergeDuplicatesResponse>>(`${MERGE_ENDPOINT}?dry=true`, {}),
    // Každé otevření načte čerstvý náhled (globální `staleTime` by ukázal starý po sloučení)
    // a chyba těžkého náhledu celé knihovny se ukáže hned (globální `retry: 2`).
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const preview = data?.data;
  const [run, setRun] = useState<MergeRunState>({ phase: "idle" });
  const started = useRef(false);
  const unmounted = useRef(false);
  // StrictMode v dev efekt připojí, uklidí a pustí znovu: připojení proto ref vrací na false,
  // jinak by `isCancelled()` platilo před první dávkou a dialog by nic neposlal.
  useEffect(() => {
    unmounted.current = false;
    return () => {
      unmounted.current = true;
    };
  }, []);

  // Z obsluhy kliknutí, ne z efektu; bez `useMutation`, jehož globální `onError` by vedle
  // chyby dialogu ukázal druhou.
  const apply = () => {
    const hashes = preview?.hashes;
    if (started.current || !hashes?.length) return;
    started.current = true;
    void runMergeBatches({
      hashes,
      post: (batch) => api.post(`${MERGE_ENDPOINT}?dry=false`, { hashes: batch }),
      onProgress: setRun,
      isCancelled: () => unmounted.current,
    });
  };

  return (
    <MergeDuplicatesView
      preview={preview}
      loading={isLoading}
      previewError={error ? error.message || String(error) : null}
      run={run}
      onApply={apply}
      onClose={() => onClose(run.phase !== "idle")}
    />
  );
}
